/**
 * AANKH - Device Hook
 * 
 * @author Harshit Khemani
 * @version 1.9.1
 * @description React hook and context for BrilliantSole SDK device management
 * 
 * This hook provides:
 * - Device connection state management
 * - Available devices discovery (for reconnection)
 * - Camera and microphone controls
 * - Video recording (combined camera + microphone)
 * - Event handling for device data
 */

import React, { 
  createContext, 
  useContext, 
  useState, 
  useEffect, 
  useCallback,
  useRef 
} from 'react';

// SDK reference
let BS = null;

/**
 * Device Context
 */
const DeviceContext = createContext(null);

/**
 * Connection status enum
 */
export const ConnectionStatus = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
};

/**
 * Microphone status enum
 */
export const MicrophoneStatus = {
  IDLE: 'idle',
  STREAMING: 'streaming',
  RECORDING: 'recording',
};

/**
 * Camera status enum
 */
export const CameraStatus = {
  IDLE: 'idle',
  TAKING_PICTURE: 'takingPicture',
  FOCUSING: 'focusing',
};

/**
 * Device Provider Component
 */
export function DeviceProvider({ children }) {
  // SDK and device refs
  const deviceRef = useRef(null);
  const deviceManagerRef = useRef(null);
  const audioContextRef = useRef(null);
  
  // Video recording refs
  const videoRecorderRef = useRef(null);
  const videoCanvasRef = useRef(null);
  const videoContextRef = useRef(null);
  const videoImageIntervalRef = useRef(null);
  const videoChunksRef = useRef([]);
  const lastVideoFrameRef = useRef(null);
  const latestCameraImageRef = useRef(null);
  
  // Camera preview refs
  const cameraPreviewIntervalRef = useRef(null);
  const [isCameraPreviewActive, setIsCameraPreviewActive] = useState(false);

  // Connection state
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(ConnectionStatus.DISCONNECTED);
  const [deviceInfo, setDeviceInfo] = useState({
    name: '',
    batteryLevel: null,
    id: null,
    type: null,
    mtu: null,
  });
  const [deviceInformation, setDeviceInformation] = useState(null);

  // Available devices for reconnection
  const [availableDevices, setAvailableDevices] = useState([]);
  const [isScanning, setIsScanning] = useState(false);
  const [devicesLoaded, setDevicesLoaded] = useState(false);

  // Camera state
  const [cameraStatus, setCameraStatus] = useState(CameraStatus.IDLE);
  const [cameraImage, setCameraImage] = useState(null);
  const [cameraProgress, setCameraProgress] = useState(0);
  const [cameraConfiguration, setCameraConfiguration] = useState(null);
  const [cameraConfigurationRanges, setCameraConfigurationRanges] = useState(null);
  const [autoPicture, setAutoPicture] = useState(false);

  // Microphone state
  const [microphoneStatus, setMicrophoneStatus] = useState(MicrophoneStatus.IDLE);
  const [isRecordingMicrophone, setIsRecordingMicrophone] = useState(false);
  const [microphoneRecording, setMicrophoneRecording] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [microphoneConfiguration, setMicrophoneConfiguration] = useState(null);
  const [microphoneConfigurationValues, setMicrophoneConfigurationValues] = useState(null);
  
  // WiFi state
  const [wifiSSID, setWifiSSIDState] = useState('');
  const [wifiPassword, setWifiPasswordState] = useState('');
  const [isWifiAvailable, setIsWifiAvailable] = useState(false);
  const [wifiConnectionEnabled, setWifiConnectionEnabled] = useState(false);
  const [isWifiConnected, setIsWifiConnected] = useState(false);
  const [ipAddress, setIpAddress] = useState('');
  
  // Video recording state
  const [isRecordingVideo, setIsRecordingVideo] = useState(false);
  const [videoRecordingUrl, setVideoRecordingUrl] = useState(null);
  const [videoRecordingProgress, setVideoRecordingProgress] = useState(0);
  const [videoRecordingDuration, setVideoRecordingDuration] = useState(0);

  /**
   * Load the BrilliantSole SDK from public directory (static file)
   * Loaded at runtime as a static asset, not bundled
   * Uses dynamic import with path constructed at runtime to prevent Vite static analysis
   */
  useEffect(() => {
    async function loadSDK() {
      try {
        // Construct SDK path using multiple variables to prevent static analysis
        // Vite cannot statically analyze this import path
        const protocol = window.location.protocol;
        const host = window.location.host;
        const folder = 'sdk';
        const filename = 'brilliantsole.module.js';
        
        // Build path dynamically
        const sdkPath = [protocol, '//', host, '/', folder, '/', filename].join('');
        
        // Load SDK from public directory as a static file (runtime import)
        // @vite-ignore tells Vite to skip this import during build/dev
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const module = await import(/* @vite-ignore */ sdkPath);
        BS = module;
        window.BS = BS;
        
        // Create device instance
        deviceRef.current = new BS.Device();
        window.device = deviceRef.current;
        
        // Get device manager for available devices
        if (BS.DeviceManager) {
          deviceManagerRef.current = BS.DeviceManager;
          window.deviceManager = deviceManagerRef.current;
        }
        
        console.log('[AANKH] BrilliantSole SDK loaded');
        setSdkLoaded(true);
      } catch (error) {
        console.error('[AANKH] Failed to load BrilliantSole SDK:', error);
        console.error('[AANKH] Error details:', error.message, error.stack);
      }
    }
    
    loadSDK();
  }, []);


  /**
   * Set up device event listeners
   */
  useEffect(() => {
    if (!sdkLoaded || !deviceRef.current) return;

    const device = deviceRef.current;

    const handleConnectionStatus = () => {
      console.log('[AANKH] Connection status:', device.connectionStatus);
      switch (device.connectionStatus) {
        case 'connected':
          setConnectionStatus(ConnectionStatus.CONNECTED);
          break;
        case 'notConnected':
          setConnectionStatus(ConnectionStatus.DISCONNECTED);
          break;
        case 'connecting':
          setConnectionStatus(ConnectionStatus.CONNECTING);
          break;
        default:
          setConnectionStatus(ConnectionStatus.DISCONNECTED);
      }
    };

    const handleConnected = () => {
      console.log('[AANKH] Device connected');
      setDeviceInfo({
        name: device.name || 'Omi Glass',
        batteryLevel: device.batteryLevel,
        id: device.id || null,
        type: device.type || null,
        mtu: device.mtu || null,
      });
      setDeviceInformation(device.deviceInformation || null);
      
      // Set up microphone if available
      if (device.hasMicrophone) {
        device.setSensorConfiguration({ microphone: 5 });
        device.setMicrophoneConfiguration({ 
          sampleRate: '16000', 
          bitDepth: '16' 
        });
        
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: 16000,
            latencyHint: 'interactive',
          });
        }
        device.audioContext = audioContextRef.current;
        device.microphoneGainNode.gain.value = 10;
        setAudioStream(device.microphoneMediaStreamDestination?.stream || null);
      }

      // Get camera configuration if available
      if (device.hasCamera && device.cameraConfiguration) {
        setCameraConfiguration(device.cameraConfiguration);
        setCameraConfigurationRanges(device.cameraConfigurationRanges || null);
      }

      // Get microphone configuration if available
      if (device.hasMicrophone && device.microphoneConfiguration) {
        setMicrophoneConfiguration(device.microphoneConfiguration);
        if (BS?.MicrophoneConfigurationValues) {
          setMicrophoneConfigurationValues(BS.MicrophoneConfigurationValues);
        }
      }

      // Get WiFi info if available
      if (device.isWifiAvailable !== undefined) {
        setIsWifiAvailable(device.isWifiAvailable);
      }
    };

    const handleBatteryLevel = () => {
      console.log('[AANKH] Battery level updated:', device.batteryLevel);
      setDeviceInfo(prev => ({
        ...prev,
        batteryLevel: device.batteryLevel,
      }));
    };

    const handleCameraStatus = () => {
      setCameraStatus(device.cameraStatus);
    };

    const handleCameraImage = (event) => {
      const imageUrl = event.message.url;
      setCameraImage(imageUrl);
      latestCameraImageRef.current = imageUrl;
      setCameraProgress(0);
    };

    const handleCameraProgress = (event) => {
      if (event.message.type === 'image') {
        setCameraProgress(event.message.progress);
      }
    };

    const handleMicrophoneStatus = () => {
      setMicrophoneStatus(device.microphoneStatus);
    };

    const handleMicrophoneRecording = (event) => {
      setMicrophoneRecording(event.message.url);
    };

    const handleIsRecordingMicrophone = () => {
      setIsRecordingMicrophone(device.isRecordingMicrophone);
    };

    const handleDeviceInformation = () => {
      setDeviceInformation(device.deviceInformation || null);
    };

    const handleGetId = () => {
      setDeviceInfo(prev => ({
        ...prev,
        id: device.id || null,
      }));
    };

    const handleGetMtu = () => {
      setDeviceInfo(prev => ({
        ...prev,
        mtu: device.mtu || null,
      }));
    };

    const handleGetName = () => {
      setDeviceInfo(prev => ({
        ...prev,
        name: device.name || 'Omi Glass',
      }));
    };

    const handleGetType = () => {
      setDeviceInfo(prev => ({
        ...prev,
        type: device.type || null,
      }));
    };

    const handleGetCameraConfiguration = () => {
      if (device.hasCamera) {
        setCameraConfiguration(device.cameraConfiguration || null);
        setCameraConfigurationRanges(device.cameraConfigurationRanges || null);
      }
    };

    const handleGetMicrophoneConfiguration = () => {
      if (device.hasMicrophone) {
        setMicrophoneConfiguration(device.microphoneConfiguration || null);
        if (BS?.MicrophoneConfigurationValues) {
          setMicrophoneConfigurationValues(BS.MicrophoneConfigurationValues);
        }
      }
    };

    const handleIsWifiAvailable = (event) => {
      setIsWifiAvailable(event.message?.isWifiAvailable || false);
    };

    const handleGetWifiSSID = (event) => {
      setWifiSSIDState(event.message?.wifiSSID || '');
    };

    const handleGetWifiPassword = (event) => {
      setWifiPasswordState(event.message?.wifiPassword || '');
    };

    const handleGetWifiConnectionEnabled = (event) => {
      setWifiConnectionEnabled(event.message?.wifiConnectionEnabled || false);
    };

    const handleIsWifiConnected = (event) => {
      setIsWifiConnected(event.message?.isWifiConnected || false);
    };

    const handleIpAddress = (event) => {
      setIpAddress(event.message?.ipAddress || '');
    };

    // Auto picture handler
    const handleCameraImageForAuto = (event) => {
      if (autoPicture && device.cameraStatus === 'idle') {
        device.takePicture().catch(error => {
          console.error('[AANKH] Auto picture failed:', error);
        });
      }
    };

    // Add event listeners
    device.addEventListener('connectionStatus', handleConnectionStatus);
    device.addEventListener('connected', handleConnected);
    device.addEventListener('batteryLevel', handleBatteryLevel);
    device.addEventListener('cameraStatus', handleCameraStatus);
    device.addEventListener('cameraImage', handleCameraImage);
    device.addEventListener('cameraImageProgress', handleCameraProgress);
    device.addEventListener('microphoneStatus', handleMicrophoneStatus);
    device.addEventListener('microphoneRecording', handleMicrophoneRecording);
    device.addEventListener('isRecordingMicrophone', handleIsRecordingMicrophone);
    device.addEventListener('deviceInformation', handleDeviceInformation);
    device.addEventListener('getId', handleGetId);
    device.addEventListener('getMtu', handleGetMtu);
    device.addEventListener('getName', handleGetName);
    device.addEventListener('getType', handleGetType);
    device.addEventListener('getCameraConfiguration', handleGetCameraConfiguration);
    device.addEventListener('getMicrophoneConfiguration', handleGetMicrophoneConfiguration);
    device.addEventListener('isWifiAvailable', handleIsWifiAvailable);
    device.addEventListener('getWifiSSID', handleGetWifiSSID);
    device.addEventListener('getWifiPassword', handleGetWifiPassword);
    device.addEventListener('getWifiConnectionEnabled', handleGetWifiConnectionEnabled);
    device.addEventListener('isWifiConnected', handleIsWifiConnected);
    device.addEventListener('ipAddress', handleIpAddress);
    device.addEventListener('cameraImage', handleCameraImageForAuto);

    return () => {
      device.removeEventListener('connectionStatus', handleConnectionStatus);
      device.removeEventListener('connected', handleConnected);
      device.removeEventListener('batteryLevel', handleBatteryLevel);
      device.removeEventListener('cameraStatus', handleCameraStatus);
      device.removeEventListener('cameraImage', handleCameraImage);
      device.removeEventListener('cameraImageProgress', handleCameraProgress);
      device.removeEventListener('microphoneStatus', handleMicrophoneStatus);
      device.removeEventListener('microphoneRecording', handleMicrophoneRecording);
      device.removeEventListener('isRecordingMicrophone', handleIsRecordingMicrophone);
      device.removeEventListener('deviceInformation', handleDeviceInformation);
      device.removeEventListener('getId', handleGetId);
      device.removeEventListener('getMtu', handleGetMtu);
      device.removeEventListener('getName', handleGetName);
      device.removeEventListener('getType', handleGetType);
      device.removeEventListener('getCameraConfiguration', handleGetCameraConfiguration);
      device.removeEventListener('getMicrophoneConfiguration', handleGetMicrophoneConfiguration);
      device.removeEventListener('isWifiAvailable', handleIsWifiAvailable);
      device.removeEventListener('getWifiSSID', handleGetWifiSSID);
      device.removeEventListener('getWifiPassword', handleGetWifiPassword);
      device.removeEventListener('getWifiConnectionEnabled', handleGetWifiConnectionEnabled);
      device.removeEventListener('isWifiConnected', handleIsWifiConnected);
      device.removeEventListener('ipAddress', handleIpAddress);
      device.removeEventListener('cameraImage', handleCameraImageForAuto);
    };
  }, [sdkLoaded, autoPicture]);

  // ============================================
  // Connection Methods
  // ============================================

  /**
   * Get available (previously connected) devices
   */
  const getAvailableDevices = useCallback(async () => {
    if (!deviceManagerRef.current?.CanGetDevices) {
      setDevicesLoaded(true);
      return [];
    }
    
    try {
      const devices = await deviceManagerRef.current.GetDevices();
      if (devices) {
        setAvailableDevices(devices);
        setDevicesLoaded(true);
        return devices;
      }
      setDevicesLoaded(true);
      return [];
    } catch (error) {
      console.error('[AANKH] Failed to get available devices:', error);
      setDevicesLoaded(true);
      return [];
    }
  }, []);

  /**
   * Auto-load available devices when SDK is loaded
   */
  useEffect(() => {
    if (sdkLoaded && !devicesLoaded) {
      getAvailableDevices();
    }
  }, [sdkLoaded, devicesLoaded, getAvailableDevices]);

  /**
   * Connect to a specific device (for reconnection)
   */
  const connectToDevice = useCallback(async (device) => {
    if (!device) return;
    
    try {
      setConnectionStatus(ConnectionStatus.CONNECTING);
      deviceRef.current = device;
      window.device = device;
      await device.connect();
    } catch (error) {
      console.error('[AANKH] Connection to device failed:', error);
      setConnectionStatus(ConnectionStatus.DISCONNECTED);
    }
  }, []);

  /**
   * Connect to new device via Web Bluetooth picker
   */
  const connect = useCallback(async () => {
    if (!deviceRef.current) {
      console.error('[AANKH] SDK not loaded');
      return;
    }
    
    try {
      setIsScanning(true);
      setConnectionStatus(ConnectionStatus.CONNECTING);
      await deviceRef.current.connect();
      setIsScanning(false);
    } catch (error) {
      console.error('[AANKH] Connection failed:', error);
      setConnectionStatus(ConnectionStatus.DISCONNECTED);
      setIsScanning(false);
    }
  }, []);

  /**
   * Disconnect from device
   */
  const disconnect = useCallback(async () => {
    if (!deviceRef.current) return;
    
    try {
      await deviceRef.current.disconnect();
    } catch (error) {
      console.error('[AANKH] Disconnect failed:', error);
    }
  }, []);

  /**
   * Toggle connection state
   */
  const toggleConnection = useCallback(async () => {
    if (connectionStatus === ConnectionStatus.CONNECTED) {
      await disconnect();
    } else if (connectionStatus === ConnectionStatus.DISCONNECTED) {
      await connect();
    }
  }, [connectionStatus, connect, disconnect]);

  /**
   * Set device name
   */
  const setDeviceName = useCallback(async (name) => {
    if (!deviceRef.current?.isConnected) return;
    
    try {
      await deviceRef.current.setName(name);
    } catch (error) {
      console.error('[AANKH] Failed to set device name:', error);
      throw error;
    }
  }, []);

  /**
   * Get device information
   */
  const getDeviceInformation = useCallback(() => {
    if (!deviceRef.current?.isConnected) return;
    
    try {
      // Trigger deviceInformation event
      // The event listener will update the state
    } catch (error) {
      console.error('[AANKH] Failed to get device information:', error);
    }
  }, []);

  /**
   * Get device ID
   */
  const getDeviceId = useCallback(() => {
    if (!deviceRef.current?.isConnected) return;
    
    try {
      // ID is available via device.id property
      // Event listener will update state
    } catch (error) {
      console.error('[AANKH] Failed to get device ID:', error);
    }
  }, []);

  /**
   * Get MTU
   */
  const getMtu = useCallback(() => {
    if (!deviceRef.current?.isConnected) return;
    
    try {
      // MTU is available via device.mtu property
      // Event listener will update state
    } catch (error) {
      console.error('[AANKH] Failed to get MTU:', error);
    }
  }, []);

  // ============================================
  // Camera Methods
  // ============================================

  const takePicture = useCallback(async () => {
    if (!deviceRef.current?.hasCamera) return;
    
    try {
      await deviceRef.current.takePicture();
    } catch (error) {
      console.error('[AANKH] Failed to take picture:', error);
    }
  }, []);

  const focusCamera = useCallback(async () => {
    if (!deviceRef.current?.hasCamera) return;
    
    try {
      await deviceRef.current.focusCamera();
    } catch (error) {
      console.error('[AANKH] Failed to focus camera:', error);
    }
  }, []);

  const updateCameraSettings = useCallback(async (settings) => {
    if (!deviceRef.current?.hasCamera) return;
    
    try {
      await deviceRef.current.setCameraConfiguration(settings);
      // Configuration will be updated via event listener
    } catch (error) {
      console.error('[AANKH] Failed to update camera settings:', error);
    }
  }, []);

  const getCameraConfiguration = useCallback(async () => {
    if (!deviceRef.current?.hasCamera) return;
    
    try {
      // Trigger getCameraConfiguration event
      // Event listener will update state
    } catch (error) {
      console.error('[AANKH] Failed to get camera configuration:', error);
    }
  }, []);

  const resetCameraConfiguration = useCallback(async () => {
    if (!deviceRef.current?.hasCamera || !cameraConfigurationRanges) return;
    
    try {
      // Reset to default values (middle of range for each setting)
      const defaultConfig = {};
      if (cameraConfigurationRanges) {
        Object.keys(cameraConfigurationRanges).forEach(key => {
          const range = cameraConfigurationRanges[key];
          defaultConfig[key] = Math.round((range.min + range.max) / 2);
        });
      }
      await deviceRef.current.setCameraConfiguration(defaultConfig);
    } catch (error) {
      console.error('[AANKH] Failed to reset camera configuration:', error);
    }
  }, [cameraConfigurationRanges]);

  const getMicrophoneConfiguration = useCallback(async () => {
    if (!deviceRef.current?.hasMicrophone) return;
    
    try {
      // Trigger getMicrophoneConfiguration event
      // Event listener will update state
    } catch (error) {
      console.error('[AANKH] Failed to get microphone configuration:', error);
    }
  }, []);

  const updateMicrophoneSettings = useCallback(async (settings) => {
    if (!deviceRef.current?.hasMicrophone) return;
    
    try {
      await deviceRef.current.setMicrophoneConfiguration(settings);
      // Configuration will be updated via event listener
    } catch (error) {
      console.error('[AANKH] Failed to update microphone settings:', error);
    }
  }, []);

  const resetMicrophoneConfiguration = useCallback(async () => {
    if (!deviceRef.current?.hasMicrophone) return;
    
    try {
      // Reset to default values
      await deviceRef.current.setMicrophoneConfiguration({
        sampleRate: '16000',
        bitDepth: '16'
      });
    } catch (error) {
      console.error('[AANKH] Failed to reset microphone configuration:', error);
    }
  }, []);

  /**
   * Start camera preview - continuously takes pictures for live preview
   */
  const startCameraPreview = useCallback(async () => {
    if (!deviceRef.current?.hasCamera || isCameraPreviewActive) return;
    
    setIsCameraPreviewActive(true);
    
    // Take initial picture
    try {
      if (deviceRef.current.cameraStatus === 'idle') {
        await deviceRef.current.takePicture();
      }
    } catch (error) {
      console.error('[AANKH] Failed to start preview:', error);
    }
    
    // Continue taking pictures at intervals (approximately 2-3 fps for preview)
    cameraPreviewIntervalRef.current = setInterval(async () => {
      if (deviceRef.current?.cameraStatus === 'idle') {
        try {
          await deviceRef.current.takePicture();
        } catch (error) {
          console.error('[AANKH] Preview picture failed:', error);
        }
      }
    }, 350); // ~2.8 fps for preview
  }, [isCameraPreviewActive]);

  /**
   * Stop camera preview
   */
  const stopCameraPreview = useCallback(() => {
    if (cameraPreviewIntervalRef.current) {
      clearInterval(cameraPreviewIntervalRef.current);
      cameraPreviewIntervalRef.current = null;
    }
    setIsCameraPreviewActive(false);
  }, []);

  // ============================================
  // Microphone Methods
  // ============================================

  const startMicrophone = useCallback(async () => {
    if (!deviceRef.current?.hasMicrophone) return;
    
    if (audioContextRef.current?.state !== 'running') {
      await audioContextRef.current?.resume();
    }
    
    try {
      await deviceRef.current.startMicrophone();
    } catch (error) {
      console.error('[AANKH] Failed to start microphone:', error);
    }
  }, []);

  const stopMicrophone = useCallback(async () => {
    if (!deviceRef.current?.hasMicrophone) return;
    
    try {
      await deviceRef.current.stopMicrophone();
    } catch (error) {
      console.error('[AANKH] Failed to stop microphone:', error);
    }
  }, []);

  const toggleMicrophoneRecording = useCallback(async () => {
    if (!deviceRef.current?.hasMicrophone) return;
    
    try {
      await deviceRef.current.toggleMicrophoneRecording();
    } catch (error) {
      console.error('[AANKH] Failed to toggle recording:', error);
    }
  }, []);

  const getMicrophoneGainNode = useCallback(() => {
    return deviceRef.current?.microphoneGainNode || null;
  }, []);

  const getAudioContext = useCallback(() => {
    return audioContextRef.current;
  }, []);

  // ============================================
  // WiFi Methods
  // ============================================

  const setWifiSSID = useCallback(async (ssid) => {
    if (!deviceRef.current?.isConnected || !deviceRef.current?.isWifiAvailable) return;
    
    try {
      await deviceRef.current.setWifiSSID(ssid);
    } catch (error) {
      console.error('[AANKH] Failed to set WiFi SSID:', error);
      throw error;
    }
  }, []);

  const setWifiPassword = useCallback(async (password) => {
    if (!deviceRef.current?.isConnected || !deviceRef.current?.isWifiAvailable) return;
    
    try {
      await deviceRef.current.setWifiPassword(password);
    } catch (error) {
      console.error('[AANKH] Failed to set WiFi password:', error);
      throw error;
    }
  }, []);

  const toggleWifiConnection = useCallback(async () => {
    if (!deviceRef.current?.isConnected || !deviceRef.current?.isWifiAvailable) return;
    
    try {
      await deviceRef.current.toggleWifiConnection();
    } catch (error) {
      console.error('[AANKH] Failed to toggle WiFi connection:', error);
      throw error;
    }
  }, []);

  // ============================================
  // Video Recording Methods
  // ============================================

  /**
   * Initialize video canvas for recording
   */
  const initVideoCanvas = useCallback(() => {
    if (!videoCanvasRef.current) {
      videoCanvasRef.current = document.createElement('canvas');
      videoCanvasRef.current.width = 1280;
      videoCanvasRef.current.height = 720;
      videoContextRef.current = videoCanvasRef.current.getContext('2d');
      videoContextRef.current.imageSmoothingEnabled = true;
      videoContextRef.current.imageSmoothingQuality = 'high';
    }
  }, []);

  /**
   * Draw image to video canvas
   */
  const drawImageToVideoCanvas = useCallback(async (imageUrl) => {
    if (!videoContextRef.current || !imageUrl) return;
    
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = videoCanvasRef.current;
        const ctx = videoContextRef.current;
        
        // Calculate dimensions to maintain aspect ratio
        const canvasAspect = canvas.width / canvas.height;
        const imageAspect = img.width / img.height;
        
        let drawWidth, drawHeight, offsetX, offsetY;
        
        if (imageAspect > canvasAspect) {
          // Image is wider - fit to width
          drawWidth = canvas.width;
          drawHeight = canvas.width / imageAspect;
          offsetX = 0;
          offsetY = (canvas.height - drawHeight) / 2;
        } else {
          // Image is taller - fit to height
          drawHeight = canvas.height;
          drawWidth = canvas.height * imageAspect;
          offsetX = (canvas.width - drawWidth) / 2;
          offsetY = 0;
        }
        
        // Fill background
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Draw image centered
        ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
        
        lastVideoFrameRef.current = imageUrl;
        resolve();
      };
      img.onerror = () => resolve();
      img.src = imageUrl;
    });
  }, []);

  /**
   * Start video recording
   */
  const startVideoRecording = useCallback(async () => {
    if (!deviceRef.current?.hasCamera || !deviceRef.current?.hasMicrophone) {
      console.error('[AANKH] Camera and microphone required for video recording');
      return;
    }
    
    try {
      // Initialize canvas
      initVideoCanvas();
      
      // Start microphone if not already started
      if (microphoneStatus !== MicrophoneStatus.STREAMING) {
        await startMicrophone();
        // Wait a bit for microphone to initialize
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // Get audio stream
      const audioContext = getAudioContext();
      const audioStream = deviceRef.current.microphoneMediaStreamDestination?.stream;
      
      if (!audioStream) {
        console.error('[AANKH] Audio stream not available');
        return;
      }
      
      // Create video stream from canvas
      const canvas = videoCanvasRef.current;
      const videoStream = canvas.captureStream(10); // 10 fps
      
      // Combine video and audio streams
      const combinedStream = new MediaStream([
        ...videoStream.getVideoTracks(),
        ...audioStream.getAudioTracks()
      ]);
      
      // Create MediaRecorder
      const options = {
        mimeType: 'video/webm;codecs=vp9',
        videoBitsPerSecond: 2500000, // 2.5 Mbps
      };
      
      // Fallback to VP8 if VP9 not supported
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options.mimeType = 'video/webm;codecs=vp8';
      }
      
      // Fallback to default if VP8 not supported
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        delete options.mimeType;
      }
      
      const recorder = new MediaRecorder(combinedStream, options);
      videoRecorderRef.current = recorder;
      videoChunksRef.current = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          videoChunksRef.current.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(videoChunksRef.current, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setVideoRecordingUrl(url);
        setVideoRecordingProgress(0);
        setVideoRecordingDuration(0);
      };
      
      recorder.start(1000); // Collect data every second
      setIsRecordingVideo(true);
      
      // Start capturing images at intervals (8 fps = 125ms for smoother recording)
      const captureInterval = 125;
      let frameCount = 0;
      videoImageIntervalRef.current = setInterval(async () => {
        try {
          // Take picture
          if (deviceRef.current?.cameraStatus === 'idle') {
            await deviceRef.current.takePicture();
            frameCount++;
            
            // Wait for image to be processed
            await new Promise(resolve => setTimeout(resolve, 80));
            
            // Use the latest camera image from ref (more reliable than state)
            const currentImage = latestCameraImageRef.current || cameraImage;
            if (currentImage) {
              await drawImageToVideoCanvas(currentImage);
            } else if (lastVideoFrameRef.current) {
              // Reuse last frame if new one not ready
              await drawImageToVideoCanvas(lastVideoFrameRef.current);
            }
          }
        } catch (error) {
          console.error('[AANKH] Error capturing frame:', error);
        }
      }, captureInterval);
      
      // Start timer
      const startTime = Date.now();
      const timerInterval = setInterval(() => {
        if (isRecordingVideo) {
          const elapsed = (Date.now() - startTime) / 1000;
          setVideoRecordingDuration(elapsed);
        } else {
          clearInterval(timerInterval);
        }
      }, 100);
      
      console.log('[AANKH] Video recording started');
    } catch (error) {
      console.error('[AANKH] Failed to start video recording:', error);
      setIsRecordingVideo(false);
    }
  }, [microphoneStatus, startMicrophone, getAudioContext, cameraImage, initVideoCanvas, drawImageToVideoCanvas, isRecordingVideo]);

  /**
   * Stop video recording
   */
  const stopVideoRecording = useCallback(async () => {
    if (!videoRecorderRef.current || !isRecordingVideo) return;
    
    try {
      // Stop recorder
      videoRecorderRef.current.stop();
      
      // Stop capturing images
      if (videoImageIntervalRef.current) {
        clearInterval(videoImageIntervalRef.current);
        videoImageIntervalRef.current = null;
      }
      
      // Stop tracks
      if (videoRecorderRef.current?.stream) {
        videoRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
      
      setIsRecordingVideo(false);
      console.log('[AANKH] Video recording stopped');
    } catch (error) {
      console.error('[AANKH] Failed to stop video recording:', error);
      setIsRecordingVideo(false);
    }
  }, [isRecordingVideo]);

  /**
   * Toggle video recording
   */
  const toggleVideoRecording = useCallback(async () => {
    if (isRecordingVideo) {
      await stopVideoRecording();
    } else {
      await startVideoRecording();
    }
  }, [isRecordingVideo, startVideoRecording, stopVideoRecording]);

  /**
   * Get video canvas element (for preview)
   */
  const getVideoCanvas = useCallback(() => {
    return videoCanvasRef.current;
  }, []);

  // Cleanup preview intervals on unmount
  useEffect(() => {
    return () => {
      if (cameraPreviewIntervalRef.current) {
        clearInterval(cameraPreviewIntervalRef.current);
      }
      if (videoImageIntervalRef.current) {
        clearInterval(videoImageIntervalRef.current);
      }
    };
  }, []);

  // ============================================
  // Context Value
  // ============================================

  const value = {
    // SDK state
    sdkLoaded,
    
    // Connection
    connectionStatus,
    isConnected: connectionStatus === ConnectionStatus.CONNECTED,
    deviceInfo,
    deviceInformation,
    availableDevices,
    devicesLoaded,
    isScanning,
    getAvailableDevices,
    connect,
    connectToDevice,
    disconnect,
    toggleConnection,
    setDeviceName,
    getDeviceInformation,
    getDeviceId,
    getMtu,
    
    // Camera
    hasCamera: sdkLoaded && deviceRef.current?.hasCamera,
    cameraStatus,
    cameraImage,
    cameraProgress,
    cameraConfiguration,
    cameraConfigurationRanges,
    autoPicture,
    setAutoPicture,
    isCameraPreviewActive,
    takePicture,
    focusCamera,
    updateCameraSettings,
    getCameraConfiguration,
    resetCameraConfiguration,
    startCameraPreview,
    stopCameraPreview,
    
    // Microphone
    hasMicrophone: sdkLoaded && deviceRef.current?.hasMicrophone,
    microphoneStatus,
    isRecordingMicrophone,
    microphoneRecording,
    audioStream,
    microphoneConfiguration,
    microphoneConfigurationValues,
    startMicrophone,
    stopMicrophone,
    toggleMicrophoneRecording,
    getMicrophoneConfiguration,
    updateMicrophoneSettings,
    resetMicrophoneConfiguration,
    getMicrophoneGainNode,
    getAudioContext,
    
    // WiFi
    isWifiAvailable: sdkLoaded && deviceRef.current?.isWifiAvailable,
    wifiSSID,
    wifiPassword,
    wifiConnectionEnabled,
    isWifiConnected,
    ipAddress,
    setWifiSSID,
    setWifiPassword,
    toggleWifiConnection,
    
    // Video Recording
    isRecordingVideo,
    videoRecordingUrl,
    videoRecordingProgress,
    videoRecordingDuration,
    startVideoRecording,
    stopVideoRecording,
    toggleVideoRecording,
    getVideoCanvas,
  };

  return (
    <DeviceContext.Provider value={value}>
      {children}
    </DeviceContext.Provider>
  );
}

/**
 * Hook to access device context
 */
export function useDevice() {
  const context = useContext(DeviceContext);
  
  if (!context) {
    throw new Error('useDevice must be used within a DeviceProvider');
  }
  
  return context;
}

export default useDevice;
